import { CategoryWithParts } from "../../models/Part";
import { IVehicle } from "../../models/Vehicle";
import { isEngineCategoryName } from "../../utils/nonFunctionalEngine";

const normalizeCategoryName = (value: string | null | undefined) =>
  (value ?? "").trim().toLowerCase();

const getTransmissionToken = (sourceVehicle: IVehicle | null) => {
  const transmission = normalizeCategoryName(
    sourceVehicle?.transmission || sourceVehicle?.combinedTransmission
  );

  if (transmission.includes("auto")) return "automatic";
  if (transmission.includes("manual")) return "manual";

  return null;
};

const getFuelTokens = (sourceVehicle: IVehicle | null) => {
  const fuel = normalizeCategoryName(
    sourceVehicle?.fuelType || sourceVehicle?.combinedFuelType
  );

  return ["diesel", "petrol", "hybrid", "electric", "lpg", "cng"].filter(token =>
    fuel.includes(token)
  );
};

const isEngineCategory = (category: CategoryWithParts) => isEngineCategoryName(category.category.name);

const isExportEngineCategory = (category: CategoryWithParts) => {
  const name = normalizeCategoryName(category.category.name);
  return isEngineCategory(category) && name.includes("export");
};

const isTransmissionAssemblyCategory = (category: CategoryWithParts) => {
  const name = normalizeCategoryName(category.category.name);
  return name === "manual transmission assembly" || name === "automatic transmission assembly";
};

const filterTopCategoryEngines = (
  sourceCategories: CategoryWithParts[],
  sourceVehicle: IVehicle | null
) => {
  const transmissionToken = getTransmissionToken(sourceVehicle);
  const fuelTokens = getFuelTokens(sourceVehicle);
  const knownFuelTokens = ["diesel", "petrol", "hybrid", "electric", "lpg", "cng"];

  const engineCategories = sourceCategories.filter(isEngineCategory);
  if (engineCategories.length === 0) return sourceCategories;

  const validEngines = engineCategories
    .map((category, index) => ({ category, index }))
    .filter(({ category }) => {
      const name = normalizeCategoryName(category.category.name);
      const categoryFuelTokens = knownFuelTokens.filter(token => name.includes(token));

      if (isExportEngineCategory(category)) return false;
      if (transmissionToken === "automatic" && name.includes("manual")) return false;
      if (transmissionToken === "manual" && name.includes("automatic")) return false;
      if (fuelTokens.length > 0 && categoryFuelTokens.length > 0) {
        return categoryFuelTokens.some(token => fuelTokens.includes(token));
      }

      return true;
    });

  if (validEngines.length === 0) {
    return sourceCategories.filter(category => !isEngineCategory(category));
  }

  const bestEngine = [...validEngines].sort((a, b) => {
    const getScore = ({ category }: { category: CategoryWithParts }) => {
      const name = normalizeCategoryName(category.category.name);
      const hasTransmissionMatch = transmissionToken != null && name.includes(transmissionToken);
      const hasFuelMatch = fuelTokens.some(token => name.includes(token));

      return (hasTransmissionMatch ? 2 : 0) + (hasFuelMatch ? 2 : 0);
    };

    return getScore(b) - getScore(a) || a.index - b.index;
  })[0].category;

  return sourceCategories.filter(category =>
    !isEngineCategory(category) || category.category.id === bestEngine.category.id
  );
};

const filterTopCategoryTransmissionAssemblies = (
  sourceCategories: CategoryWithParts[],
  sourceVehicle: IVehicle | null
) => {
  const transmissionCategories = sourceCategories.filter(isTransmissionAssemblyCategory);
  if (transmissionCategories.length === 0) return sourceCategories;

  const transmissionToken = getTransmissionToken(sourceVehicle);

  const validTransmissions = transmissionCategories
    .map((category, index) => ({ category, index }))
    .filter(({ category }) => {
      const name = normalizeCategoryName(category.category.name);

      if (transmissionToken === "automatic" && name.includes("manual")) return false;
      if (transmissionToken === "manual" && name.includes("automatic")) return false;

      return true;
    });

  if (validTransmissions.length === 0) {
    return sourceCategories.filter(category => !isTransmissionAssemblyCategory(category));
  }

  const bestTransmission = [...validTransmissions].sort((a, b) => {
    const getScore = ({ category }: { category: CategoryWithParts }) => {
      const name = normalizeCategoryName(category.category.name);
      return transmissionToken != null && name.includes(transmissionToken) ? 1 : 0;
    };

    return getScore(b) - getScore(a) || a.index - b.index;
  })[0].category;

  return sourceCategories.filter(category =>
    !isTransmissionAssemblyCategory(category) || category.category.id === bestTransmission.category.id
  );
};

export const filterTopCategories = (
  sourceCategories: CategoryWithParts[],
  sourceVehicle: IVehicle | null
) => filterTopCategoryTransmissionAssemblies(
  filterTopCategoryEngines(sourceCategories, sourceVehicle),
  sourceVehicle
);
